/**
 * ComposeAction — discriminated union for all compose-affordance actions.
 * Four kinds:
 *   - fire    : chips, sendMessage immediately
 *   - insert  : templates, requestInsert into textarea
 *   - navigate: Platform links, route change
 *   - exec    : v0.1 substrate exercisers (Hill Climbers bench) — call
 *               a backend endpoint with sessionId + toast feedback.
 *               Function-typed `run` instead of a prompt string.
 */
export type ExecActionContext = {
  sessionId: string | undefined;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
};

export type ComposeAction =
  | {
      kind: 'fire';
      id: string;
      label: string;
      prompt: string;
      descriptor?: string;
      emoji?: string;
    }
  | {
      kind: 'insert';
      id: string;
      label: string;
      prompt: string;
      descriptor?: string;
      emoji?: string;
    }
  | {
      kind: 'navigate';
      id: string;
      label: string;
      route: string;
      descriptor?: string;
      emoji?: string;
    }
  | {
      kind: 'exec';
      id: string;
      label: string;
      descriptor?: string;
      emoji?: string;
      run: (ctx: ExecActionContext) => Promise<void>;
    };

/**
 * ComposeTab — flat list of actions per tab. No nestedTabs field; Lab's
 * 7-bench nesting is private to labTab.tsx (per spec-triad: premature
 * generalization for one consumer).
 */
export interface ComposeTab {
  id: string;
  emoji: string;
  label: string;
  descriptor: string;
  actions: ComposeAction[];
}

export interface ActionDispatchContext {
  sendMessage: (prompt: string, metadata?: Record<string, any>) => Promise<void>;
  requestInsert: (text: string) => void;
  navigate: (route: string) => void;
  // v0.1 Item 8 — exec actions need session id + toast channel.
  exec: ExecActionContext;
}

/**
 * Dispatch a ComposeAction by kind. Exhaustive default: never arm
 * defends against silent runtime no-ops when a 5th kind is added later
 * without updating this switch (compile-time guard).
 */
export function dispatchAction(
  action: ComposeAction,
  ctx: ActionDispatchContext,
): void {
  switch (action.kind) {
    case 'fire':
      ctx.sendMessage(action.prompt, {
        template: { id: action.id, label: action.label, source: 'compose_panel' },
      });
      break;
    case 'insert':
      ctx.requestInsert(action.prompt);
      break;
    case 'navigate':
      ctx.navigate(action.route);
      break;
    case 'exec':
      // Fire-and-track via the action's own run; errors surface via
      // ctx.exec.showToast. Caller (ComposePanel) doesn't await.
      void action.run(ctx.exec).catch((e) => {
        ctx.exec.showToast(
          `${action.label}: ${(e as Error).message ?? 'failed'}`,
          'error',
        );
      });
      break;
    default: {
      const _exhaustive: never = action;
      throw new Error(
        `unhandled ComposeAction kind: ${(_exhaustive as { kind: string }).kind}`,
      );
    }
  }
}
